/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import { FieldArray, FormikTouched, useFormikContext } from 'formik';
import { DataPath, Modem, TerminalFormSchema, TerminalType } from '../../../shared/entities/provisionedTerminal';
import { AccordionDetails, AccordionSummary, Box, IconButton } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BeamInputLabel } from '../../common/formik/BeamInputLabel';
import { BeamSelect } from '../../common/formik/BeamSelect';
import { selectAntennaTypeOptions, selectDatapathLabelOptions } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import {
  requestAntennaOptions,
  requestDatapathLabelOptions,
  requestNetworkOptions,
} from '../../../store/options/actions';
import { BeamAccordion } from '../../common/beam/BeamAccordion';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { DeepPartial } from 'redux';
import ProvisioningAddButton from '../ProvisioningAddButton';
import { Delete, Warning } from '@mui/icons-material';
import appTheme from '../../common/theme/appTheme.json';
import ModalDialog from '../../common/dialogs/ModalDialog';
import ModemBuilder from './ModemBuilder';
import { AntennaOptions } from '../../../shared/entities/options/antennas';

const getAntennaByNameOrDisplayName = (antennaTypes: AntennaOptions | undefined, antennaValue?: string | null) => {
  if (antennaTypes === undefined || antennaValue === undefined || antennaValue === null || antennaValue === '') {
    return undefined;
  }

  return antennaTypes.find(
    antennaType => antennaType.name === antennaValue || antennaType.displayName === antennaValue,
  );
};

const getAntennaName = (antennaTypes: AntennaOptions | undefined, antennaValue?: string | null) => {
  return getAntennaByNameOrDisplayName(antennaTypes, antennaValue)?.name ?? antennaValue;
};

const getAntennaDisplayName = (antennaTypes: AntennaOptions | undefined, antennaValue?: string | null) => {
  return getAntennaByNameOrDisplayName(antennaTypes, antennaValue)?.displayName ?? antennaValue ?? '';
};

// Define the mapping from terminal types to default data path labels
interface DatapathConfig {
  wantedLabels?: string[];
  defaultDatapathCount: number;
}

const TT_TO_DP_CONFIG: Record<TerminalType, DatapathConfig> = {
  Single: {
    defaultDatapathCount: 1,
  },
  Dual: {
    wantedLabels: ['CWS1', 'CWS2'],
    defaultDatapathCount: 2,
  },
  'Ku+Ka': {
    wantedLabels: ['KU', 'KA'],
    defaultDatapathCount: 2,
  },
  HAN: {
    defaultDatapathCount: 1,
  },
  HaloNet: {
    // Temporary until we need to support more transport paths for HaloNet space vehicles
    wantedLabels: ['VI', 'RTE'],
    defaultDatapathCount: 2,
  },
};

// Terminal types that use the dynamic form (user can add/remove datapaths and modems at-will).
// HAN and HaloNet both allow an arbitrary number of datapaths/modems.
const isDynamicDatapathType = (terminalType: string): boolean => terminalType === 'HAN' || terminalType === 'HaloNet';

export const DataPaths = () => {
  const {
    values: { datapaths, terminalType },
    touched: formikTouched,
    setFieldValue,
    setTouched,
  } = useFormikContext<TerminalFormSchema>();

  // Fetch dropdown options
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(requestAntennaOptions());
    dispatch(requestNetworkOptions());
    dispatch(requestDatapathLabelOptions());
  }, [dispatch]);

  // Extract the uuid from the URL parameters to determine if we are in edit mode
  let { uuid } = useParams();
  const isEditMode = uuid !== undefined;

  // State to track the last terminal type selected
  const [lastTerminalType, setLastTerminalType] = useState<TerminalType>(terminalType);

  // Get the antenna types from the store
  const antennaTypes = useSelector(selectAntennaTypeOptions);

  // Get the antenna types that are multiband (ie require two modems)
  const multibandAntennaNames = useMemo(() => {
    return antennaTypes?.filter(antennaType => antennaType.multiband).map(antennaType => antennaType.name);
  }, [antennaTypes]);

  // Before auto-populating DataPathBuilder form fields for an existing terminal, we need to ensure the
  // antenna value corresponds to antenna name and not antenna display name.
  // This is necessary because the form allows users to select antennas by their display name, but the backend expects antenna names.
  // Likely overkill but this handles the edge case where some saved datapaths in provisioning database use antenna name while others use display name.
  useEffect(() => {
    if (antennaTypes === undefined || datapaths === undefined || datapaths.length === 0) return;

    const normalizedDatapaths = datapaths.map(datapath => {
      const normalizedAntennaName = getAntennaName(antennaTypes, datapath.antenna);
      if (normalizedAntennaName === datapath.antenna) {
        return datapath;
      }

      return {
        ...datapath,
        antenna: normalizedAntennaName,
      };
    });

    if (!_.isEqual(datapaths, normalizedDatapaths)) {
      setFieldValue('datapaths', normalizedDatapaths);
    }
  }, [antennaTypes, datapaths, setFieldValue]);

  // Show dynamic form (i.e. user can configure n number of datapaths) for HAN and HaloNet terminals
  const dynamicFormEnabled = useMemo(() => {
    return isDynamicDatapathType(terminalType);
  }, [terminalType]);

  // Update data paths based on the new terminal type selected
  const updateDataPathsAndTrimModemsOnTerminalTypeChange = useCallback(
    (terminalType: string) => {
      // Get the default data path configuration for the selected terminal type
      const defaultDatapathConfig = TT_TO_DP_CONFIG[terminalType] ?? {
        defaultDatapathCount: 0,
      };
      // Extract the wanted labels from the configuration
      const wantedDataPathLabels = defaultDatapathConfig.wantedLabels ?? [];

      // Determine how many data paths are wanted based on the terminal type (and if we're in edit mode)
      const wantedDataPathCount =
        isEditMode && dynamicFormEnabled ? datapaths.length : defaultDatapathConfig.defaultDatapathCount;

      // Ensure there are enough data paths for the required labels
      let newDatapaths = [...datapaths] as DeepPartial<DataPath>[];
      if (wantedDataPathCount > newDatapaths.length) {
        // Create new empty data paths to match the required count
        const newEmptyDatapaths = Array.from({ length: wantedDataPathCount - newDatapaths.length }, () => ({
          label: undefined,
          modems: [{ networks: [] }],
        }));
        newDatapaths = [...newDatapaths, ...newEmptyDatapaths];
      } else if (wantedDataPathCount < newDatapaths.length) {
        // Remove excess data paths
        newDatapaths = newDatapaths.slice(0, wantedDataPathCount);
      }

      // Auto-apply labels to data paths that don't have them yet, if configured
      if (wantedDataPathLabels.length > 0) {
        const labelsToAutoApply = [...wantedDataPathLabels];
        newDatapaths.forEach((datapath: DeepPartial<DataPath>, index) => {
          if (datapath.label && labelsToAutoApply.includes(datapath.label)) {
            // Get remove element from array that corresponds to datapath's existing label
            const idx = labelsToAutoApply.indexOf(datapath.label);
            labelsToAutoApply.splice(idx, 1);
          } else {
            datapath.label = labelsToAutoApply.shift();
          }
        });
      }

      // Go through datapaths and ensure that each one has a max of one or two modems based on terminal type and antenna type
      newDatapaths = newDatapaths.map((datapath, datapathIdx) => {
        const datapathAntennaName = getAntennaName(antennaTypes, datapath.antenna);

        // Determine the amount of modems required for the selected antenna (except when terminal type is Ku+Ka, which is stricter)
        const requiredModemCount =
          datapathAntennaName &&
          multibandAntennaNames &&
          multibandAntennaNames.includes(datapathAntennaName) &&
          terminalType !== 'Ku+Ka'
            ? 2
            : 1;

        if (datapath.modems) {
          // Remove excess modems
          if (!isDynamicDatapathType(terminalType) && datapath.modems.length > requiredModemCount) {
            const remainingModems = datapath.modems.slice(0, requiredModemCount);
            remainingModems.forEach(modem => {
              if (modem?.type) {
                modem.type = undefined;
              }
            });

            datapath.modems = [...remainingModems];
          }

          // Add missing modems
          if (datapath.modems.length < requiredModemCount) {
            const newEmptyModems = Array.from({ length: requiredModemCount - datapath.modems.length }, () => ({
              networks: [],
            }));
            datapath.modems = [...datapath.modems, ...newEmptyModems];
          }
        }

        // Reset the touched state for all datapath fields
        const touched = { ...formikTouched };
        if (touched.datapaths) {
          touched.datapaths[datapathIdx] = {
            ...touched.datapaths[datapathIdx],
            antenna: undefined,
            modems: touched.datapaths[datapathIdx]?.modems?.map(_touchedModem => ({
              type: undefined,
              networks: undefined,
              satIp: undefined,
              macAddress: undefined,
            })),
          } as FormikTouched<DataPath>;

          setTouched(touched);
        }

        return datapath;
      });

      // Makes sure the new data paths are not the same as the old ones
      if (!_.isEqual(datapaths, newDatapaths)) {
        // Update the form field with the new data paths
        setFieldValue('datapaths', newDatapaths);
      }
    },
    [
      datapaths,
      isEditMode,
      dynamicFormEnabled,
      multibandAntennaNames,
      formikTouched,
      setTouched,
      setFieldValue,
      antennaTypes,
    ],
  );

  // Effect hook to update the data paths when the terminal type changes
  useEffect(() => {
    // If the terminal type hasn't changed, there's no need to update
    if (terminalType === lastTerminalType) return;
    setLastTerminalType(terminalType);

    // Update data paths based on the new terminal type
    updateDataPathsAndTrimModemsOnTerminalTypeChange(terminalType);
  }, [terminalType, lastTerminalType, updateDataPathsAndTrimModemsOnTerminalTypeChange]);

  // Initial implementation requires terminalType to be set which will cause datapaths.length to be g.t. 0.
  // This is because we didn't want the initial implementation to be dynamic, i.e., a user can add/remove
  // datapaths and modems at-will. Instead, it will pre-populate the form inputs based on the terminal type
  // that is selected. Therefore, until a terminal type is set, the component will be hidden.
  if (datapaths === undefined || datapaths.length === 0) return <></>;

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <BeamInputLabel label={'Data Paths'} htmlFor={'datapaths'} required={true} />
      <FieldArray name={'datapaths'}>
        {({ remove, push }) => {
          return (
            <div>
              {datapaths.length > 0 &&
                datapaths.map((datapath, datapathIdx) => (
                  <DataPathBuilder
                    key={`${datapathIdx}-${datapath?.label ?? 'not-set'}-${datapath?.antenna ?? 'not-set'}`}
                    antennaTypes={antennaTypes}
                    multibandAntennaNames={multibandAntennaNames}
                    datapath={datapath}
                    datapathIdx={datapathIdx}
                    setFieldValue={setFieldValue}
                    terminalType={terminalType}
                    dynamicFormEnabled={dynamicFormEnabled}
                    deleteEnabled={
                      !!terminalType && // Terminal type is set
                      datapaths.length > TT_TO_DP_CONFIG[terminalType].defaultDatapathCount && // There's more than the default amount of data paths
                      dynamicFormEnabled && // Dynamic form is enabled
                      datapathIdx === datapaths.length - 1 // Only the last data path can be deleted
                    }
                    onDelete={() => {
                      remove(datapathIdx);
                    }}
                  />
                ))}
              {dynamicFormEnabled && (
                <ProvisioningAddButton
                  data-testid="add-data-path"
                  text={'Add Data Path'}
                  onClick={() => {
                    push({ label: undefined, modems: [{ networks: [] }] });
                  }}
                />
              )}
            </div>
          );
        }}
      </FieldArray>
    </Box>
  );
};

const DataPathBuilder = ({
  antennaTypes,
  multibandAntennaNames,
  datapath,
  datapathIdx,
  setFieldValue,
  terminalType,
  dynamicFormEnabled,
  deleteEnabled,
  onDelete,
}: {
  antennaTypes: AntennaOptions | undefined;
  multibandAntennaNames: string[] | undefined;
  datapath: DataPath;
  datapathIdx: number;
  setFieldValue: Function;
  terminalType: string;
  dynamicFormEnabled: boolean;
  deleteEnabled: boolean;
  onDelete: (datapathIdx: number) => void;
}) => {
  const formikContext = useFormikContext<TerminalFormSchema>();
  let datapathLabelOptions = useSelector(selectDatapathLabelOptions);

  // State to track if the delete modal should be shown
  const [showDeleteDataPathModal, setShowDeleteDataPathModal] = useState(false);

  // For 'Single', 'HAN', or 'HaloNet', derive the label from the antenna unless the terminal type fixes it (VI/RTE).
  const isLabelFixedByTerminalType = !!TT_TO_DP_CONFIG[terminalType]?.wantedLabels?.includes(datapath.label);
  const canDisplayLabelSelector =
    (terminalType === 'Single' || terminalType === 'HAN' || terminalType === 'HaloNet') && !isLabelFixedByTerminalType;
  const datapathLabelForAntenna: string | undefined = getAntennaByNameOrDisplayName(
    antennaTypes,
    datapath.antenna,
  )?.datapathLabel;
  const useLabelFromAntenna =
    canDisplayLabelSelector && datapathLabelForAntenna !== undefined && datapathLabelForAntenna !== null;
  if (useLabelFromAntenna && datapath.label !== datapathLabelForAntenna) {
    setFieldValue(`datapaths[${datapathIdx}].label`, datapathLabelForAntenna);
  }

  const isAntennaFieldTouched = formikContext.getFieldMeta(`datapaths[${datapathIdx}].antenna`).touched;
  let { uuid } = useParams();
  const isEditMode = uuid !== undefined;

  const updateModemsForMultiBandKuKaTerminal = useCallback(
    (antennaName: string | null) => {
      // Ku+Ka doesn't need this logic. The layout of that terminal type is stricter.
      if (terminalType === 'Ku+Ka') return;

      let newModems: Partial<Modem>[] = [];
      if (antennaName && multibandAntennaNames && multibandAntennaNames.includes(antennaName)) {
        // Separate networks across two separate modem entities when a multi-band antenna is in use
        newModems = [
          { networks: ['Arclight-1'], satIp: datapath.modems[0].satIp, macAddress: null },
          { networks: ['Viasat'], satIp: null, macAddress: datapath.modems[0].macAddress },
        ];
      } else {
        newModems = [{ networks: [], satIp: null, macAddress: null }];
      }

      // Update the form field with the new modems
      setFieldValue(`datapaths[${datapathIdx}].modems`, newModems);
    },
    [datapath.modems, datapathIdx, multibandAntennaNames, setFieldValue, terminalType],
  );

  // Use antenna names as values while rendering display names as the label, in A-Z order.
  let antennaOptions = [...(antennaTypes ?? [])]
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map(antennaType => antennaType.name);

  if (terminalType === 'Ku+Ka') {
    // For Ku+Ka terminals, filter the selectable antenna options based on the data path label
    antennaOptions =
      antennaTypes
        ?.filter(antennaType => (antennaType.datapathLabel ?? '').includes(datapath.label))
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map(antennaType => antennaType.name) ?? [];
  }

  return (
    <>
      <BeamAccordion key={datapath.label} defaultExpanded>
        <AccordionSummary>
          {`Data Path #${datapathIdx + 1}${datapath.label && datapath.label.length > 0 ? `: ${datapath.label}` : ''}`}
          {deleteEnabled && (
            <IconButton
              data-testid={`delete-datapath-${datapathIdx}`}
              onClick={event => {
                event.stopPropagation();
                setShowDeleteDataPathModal(true);
              }}
              sx={{ padding: 0, marginLeft: 'auto' }}
            >
              <Delete sx={{ color: appTheme.primaryTextColor, fontSize: 24 }} />
            </IconButton>
          )}
        </AccordionSummary>

        <AccordionDetails style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <BeamSelect
            name={`datapaths[${datapathIdx}].antenna`}
            label={'Antenna Type'}
            options={antennaOptions ?? []}
            placeholder={'Select an antenna type'}
            required={true}
            getOptionLabel={option => getAntennaDisplayName(antennaTypes, option)}
            isOptionEqualToValue={(option, value) => {
              const optionName = getAntennaName(antennaTypes, option);
              const valueName = getAntennaName(antennaTypes, value);
              return optionName !== undefined && optionName === valueName;
            }}
            onChange={(_e, newAntennaType) => {
              const normalizedAntennaName = getAntennaName(antennaTypes, newAntennaType);
              updateModemsForMultiBandKuKaTerminal(normalizedAntennaName ?? null);
              return normalizedAntennaName;
            }}
          />

          <FieldArray name={`datapaths[${datapathIdx}].modems`}>
            {({ remove, push }) => {
              return (
                <>
                  {datapath.modems.length > 0 &&
                    datapath.modems.map((modem, modemIdx) => (
                      <ModemBuilder
                        key={`${datapathIdx}-${modemIdx}`}
                        modem={modem}
                        modemIdx={modemIdx}
                        datapath={datapath}
                        datapathIdx={datapathIdx}
                        setFieldValue={setFieldValue}
                        deleteEnabled={
                          datapath.modems.length > 1 && // There's more than one modem (don't want users to delete the last remaining modem)
                          dynamicFormEnabled && // Dynamic form is enabled
                          modemIdx === datapath.modems.length - 1 // Only the last modem can be deleted
                        }
                        terminalType={terminalType}
                        onDelete={() => {
                          // Removes modem input fields from the form
                          remove(modemIdx);
                        }}
                      />
                    ))}
                  {dynamicFormEnabled && datapath.modems.length > 0 && datapath.antenna && (
                    <ProvisioningAddButton
                      data-testid="add-modem"
                      text={'Add Modem'}
                      onClick={() => {
                        // Adds modem input fields to the form
                        push({ networks: [] });
                      }}
                      sx={{
                        marginRight: 'auto', // Push button all the way to the left
                        paddingLeft: '0px',
                      }}
                    />
                  )}
                </>
              );
            }}
          </FieldArray>

          {((!useLabelFromAntenna && isAntennaFieldTouched) || (isEditMode && datapathLabelForAntenna === undefined)) &&
            canDisplayLabelSelector && (
              <BeamSelect
                name={`datapaths[${datapathIdx}].label`}
                label={'Data Path Label'}
                options={[...(datapathLabelOptions ?? [])].sort((a, b) => a.localeCompare(b))}
                placeholder={'Select a data path label'}
                required={true}
              />
            )}
        </AccordionDetails>
      </BeamAccordion>
      {deleteEnabled && (
        <ModalDialog
          open={showDeleteDataPathModal}
          onLeftBtnClicked={() => {
            setShowDeleteDataPathModal(false);
          }}
          onRightBtnClicked={() => {
            onDelete(datapathIdx);
            setShowDeleteDataPathModal(false);
          }}
          title={
            <Box display={'flex'} gap={'8px'} alignItems={'center'}>
              <Warning htmlColor={'#FFB029'} /> Confirm Data Path Deletion
            </Box>
          }
          message={
            <>
              Are you sure you want to delete this data path?
              <br />
              <br />
              This action cannot be undone.
            </>
          }
        />
      )}
    </>
  );
};
